import React, { useContext, useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { AuthContext } from '../contexts/AuthContext';
import SupportDialog from './SupportDialog';
import { Logo } from './design';
import { AmberButton } from './design';

const NavLink = ({ to, children, active = false }) => (
  <Box
    component={RouterLink}
    to={to}
    sx={{
      textDecoration: 'none',
      color: active ? '#f4f8ff' : '#a9bcdc',
      fontSize: '14px',
      fontWeight: 500,
      px: '14px',
      py: '8px',
      borderRadius: '8px',
      background: active ? 'rgba(120,150,200,.1)' : 'transparent',
      transition: 'color .15s, background .15s',
      '&:hover': { color: '#f4f8ff', background: 'rgba(120,150,200,.1)' },
    }}
  >
    {children}
  </Box>
);

const Navbar = () => {
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setAnchorElUser(null);
  };

  const initial = user?.username?.charAt(0).toUpperCase() || '?';

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '28px',
          height: '64px',
          background: 'rgba(10,19,38,.86)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(120,150,200,.16)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
          <Logo size={30} to={isAuthenticated ? '/dashboard' : '/'} />
          {/* Desktop links */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '4px' }}>
            {isAuthenticated ? (
              <>
                <NavLink to="/dashboard" active={location.pathname === '/dashboard'}>Drawings</NavLink>
                <NavLink to="/upload" active={location.pathname === '/upload'}>Upload</NavLink>
              </>
            ) : (
              <>
                <NavLink to="/learn-more" active={location.pathname === '/learn-more'}>How it works</NavLink>
                <NavLink to="/pricing" active={location.pathname === '/pricing'}>Pricing</NavLink>
                <NavLink to="/login" active={location.pathname === '/login'}>Log in</NavLink>
              </>
            )}
          </Box>
        </Box>

        {/* Right side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {isAuthenticated ? (
            <>
              <Box
                onClick={(e) => setAnchorElUser(e.currentTarget)}
                sx={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  background: 'rgba(45,212,191,.16)',
                  border: '1px solid rgba(45,212,191,.5)',
                  color: '#2dd4bf',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                {initial}
              </Box>
              <Menu
                anchorEl={anchorElUser}
                open={Boolean(anchorElUser)}
                onClose={() => setAnchorElUser(null)}
                sx={{ '& .MuiPaper-root': { background: '#0d1a33', border: '1px solid rgba(120,150,200,.22)', mt: '8px' } }}
              >
                <MenuItem onClick={() => { navigate('/profile'); setAnchorElUser(null); }} sx={{ color: '#dce6fa', fontSize: '14px' }}>Profile</MenuItem>
                <MenuItem onClick={() => { setSupportOpen(true); setAnchorElUser(null); }} sx={{ color: '#dce6fa', fontSize: '14px' }}>Get Help</MenuItem>
                <MenuItem onClick={handleLogout} sx={{ color: '#f4716a', fontSize: '14px' }}>Sign out</MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              <AmberButton to="/register" size="sm">Get started</AmberButton>
            </Box>
          )}

          {/* Mobile hamburger */}
          <IconButton
            sx={{ display: { md: 'none' }, color: '#a9bcdc' }}
            onClick={() => setMobileOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { background: '#0a1326', width: 240, borderLeft: '1px solid rgba(120,150,200,.18)' } }}
      >
        <List sx={{ pt: '24px' }}>
          {isAuthenticated ? (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}>
                  <ListItemText primary="Drawings" primaryTypographyProps={{ sx: { color: '#dce6fa', fontWeight: 500 } }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/upload'); setMobileOpen(false); }}>
                  <ListItemText primary="Upload" primaryTypographyProps={{ sx: { color: '#dce6fa', fontWeight: 500 } }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/profile'); setMobileOpen(false); }}>
                  <ListItemText primary="Profile" primaryTypographyProps={{ sx: { color: '#dce6fa', fontWeight: 500 } }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { setSupportOpen(true); setMobileOpen(false); }}>
                  <ListItemText primary="Get Help" primaryTypographyProps={{ sx: { color: '#dce6fa', fontWeight: 500 } }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { handleLogout(); setMobileOpen(false); }}>
                  <ListItemText primary="Sign out" primaryTypographyProps={{ sx: { color: '#f4716a', fontWeight: 500 } }} />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/learn-more'); setMobileOpen(false); }}>
                  <ListItemText primary="How it works" primaryTypographyProps={{ sx: { color: '#dce6fa', fontWeight: 500 } }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/pricing'); setMobileOpen(false); }}>
                  <ListItemText primary="Pricing" primaryTypographyProps={{ sx: { color: '#dce6fa', fontWeight: 500 } }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/login'); setMobileOpen(false); }}>
                  <ListItemText primary="Log in" primaryTypographyProps={{ sx: { color: '#dce6fa', fontWeight: 500 } }} />
                </ListItemButton>
              </ListItem>
              <ListItem sx={{ pt: '16px' }}>
                <AmberButton to="/register" fullWidth onClick={() => setMobileOpen(false)}>Get started</AmberButton>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>

      <SupportDialog open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
};

export default Navbar;
